import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getReleaseNotes,
  type ReleaseCategory,
} from "@/lib/releaseNotes";
import { getOperator } from "@/lib/auth";
import "./settings-content.css";

/**
 * §software-updates — tenant-facing "What's New" surface, living in Settings
 * (the standard SaaS home for product updates + version). The layout/styling is
 * a pixel-for-pixel port of the design reference (web/bg-variations.html ·
 * .content / .card / .feat / .tag), see settings-content.css. XPAND ships as one
 * shared, auto-updated image, so there is no "install" action — this informs, it
 * doesn't act. The feed is the notes Mission Control publishes; the version line
 * is the real deployed build (deployment_info).
 */

const CAT_LABEL: Record<ReleaseCategory, string> = {
  added: "New",
  improved: "Improved",
  changed: "Changed",
  fixed: "Fixed",
  security: "Security",
};

function fmtDate(epoch?: number | null): string {
  if (!epoch) return "";
  try {
    return new Date(epoch * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function seenKey(): string {
  const op = getOperator();
  return `xpand.whatsnew.seen.${op?.tenant_id || "t"}.${op?.id ?? "o"}`;
}

export function SoftwareUpdatesPage() {
  const q = useQuery({ queryKey: ["release-notes"], queryFn: getReleaseNotes, staleTime: 60_000 });
  const notes = q.data?.notes ?? [];
  const version = q.data?.version;

  // Viewing this dedicated surface clears the unread indicator everywhere.
  useEffect(() => {
    const latest = notes.reduce((m, n) => Math.max(m, n.publishedAt || 0), 0);
    if (latest > 0) localStorage.setItem(seenKey(), String(latest));
  }, [notes]);

  return (
    <div className="wn-shell flex-1 overflow-y-auto">
      <h1 className="wn-h1">What&rsquo;s new</h1>
      <p className="wn-lede">
        Product updates and improvements to your AI agent. ORIKIN AI updates
        automatically — there&rsquo;s nothing to install.
      </p>

      {/* Version status — the real deployed build. */}
      <div className="wn-card">
        <div className="wn-row">
          <svg className="wn-check" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth={1.6} aria-hidden>
            <circle cx="12" cy="12" r="9" />
            <path d="m8.5 12 2.5 2.5 5-5" />
          </svg>
          <div>
            <div className="wn-ttl">You&rsquo;re on the latest version</div>
            <div className="wn-meta">
              {version?.hasBuildMarker ? `build ${version.shortSha}` : "managed deployment"}
              {version?.deployedAt ? ` · updated ${fmtDate(version.deployedAt)}` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Release-note feed (published from Mission Control). */}
      {q.isLoading ? (
        <div className="wn-card">
          <div className="wn-body">Loading updates…</div>
        </div>
      ) : notes.length === 0 ? (
        <div className="wn-card">
          <div className="wn-ttl">No product updates yet.</div>
          <div className="wn-body" style={{ marginTop: 6 }}>
            New features and improvements will appear here as we ship them.
          </div>
        </div>
      ) : (
        notes.map((n) => (
          <div className="wn-card" key={n.id}>
            <div className="wn-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="wn-ttl">{n.title}</div>
              </div>
              <div className="wn-date">
                {n.version}
                {n.publishedAt ? ` · ${fmtDate(n.publishedAt)}` : ""}
              </div>
            </div>
            {n.summary ? (
              <p className="wn-body" style={{ marginTop: 10 }}>{n.summary}</p>
            ) : null}
            {n.items.map((it, i) => (
              <div className="wn-feat" key={i}>
                <span className="wn-tag" data-cat={it.category}>
                  {CAT_LABEL[it.category] || it.category}
                </span>
                <span>{it.text}</span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

/**
 * Tiny unread indicator for the sidebar "What's new" nav item — a subtle accent
 * dot when there are unseen published notes; clears once the operator opens the
 * What's-new tab.
 */
export function SoftwareUpdateDot() {
  const q = useQuery({ queryKey: ["release-notes"], queryFn: getReleaseNotes, staleTime: 60_000 });
  const notes = q.data?.notes ?? [];
  const lastSeen = Number(localStorage.getItem(seenKey()) || 0);
  const unread = notes.some((n) => (n.publishedAt || 0) > lastSeen);
  if (!unread) return null;
  return (
    <span
      className="ml-auto h-2 w-2 shrink-0 rounded-full"
      style={{ background: "var(--platform-accent, #8b7ad6)" }}
      aria-label="New product updates"
      title="New product updates"
    />
  );
}

export default SoftwareUpdatesPage;
